import React, { useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, User, Package, Heart, LogOut } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { API } from "@/lib/api";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/wishlist").then((r) => setWishlist(r.data));
  }, [user]);

  if (loading) return <div className="container-luxe py-32">Loading…</div>;
  if (!user) return <Navigate to="/login" />;

  const download = async (order_id, product_id) => {
    try {
      const token = localStorage.getItem("playa_token");
      const res = await fetch(`${API}/download/${order_id}/${product_id}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Download failed");
      const ctype = res.headers.get("Content-Type") || "";
      if (ctype.includes("application/json")) {
        const j = await res.json();
        if (j.type === "url" && j.url) {
          window.open(j.url, "_blank", "noopener,noreferrer");
          toast.success(`Link opened · ${j.downloads_remaining} accesses remaining`);
          return;
        }
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `playa-${product_id}.bin`; a.click();
    } catch (e) { toast.error(e.message); }
  };

  const tabs = [
    { k: "orders", t: "Orders", i: Package },
    { k: "downloads", t: "Downloads", i: Download },
    { k: "wishlist", t: "Wishlist", i: Heart },
    { k: "profile", t: "Profile", i: User },
  ];

  return (
    <div className="container-luxe py-16">
      <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Account</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold">Welcome, {user.name?.split(" ")[0] || "Client"}</h1>
        </div>
        <button onClick={logout} className="btn-ghost text-sm"><LogOut size={14} /> Logout</button>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="glass rounded-2xl p-4 h-fit">
          {tabs.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setSp({ tab: t.k })} className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 text-sm transition ${tab === t.k ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-gray-300 hover:text-white"}`}>
              <t.i size={16} /> {t.t}
            </button>
          ))}
        </aside>

        <div className="glass rounded-2xl p-8">
          {tab === "orders" && (
            <>
              <h2 className="font-display text-2xl mb-6">Order History</h2>
              {orders.length === 0 ? <p className="text-gray-500">No orders yet.</p> :
                orders.map((o) => (
                  <div key={o.id} data-testid={`order-${o.id}`} className="border-b border-white/5 py-5">
                    <div className="flex justify-between text-sm">
                      <span>Order #{o.id.slice(0, 8)}</span>
                      <span className={o.payment_status === "paid" ? "text-[#00A86B]" : "text-gray-400"}>{o.status}</span>
                    </div>
                    <div className="text-gray-400 text-sm mt-1">{o.items?.length} item(s) · ${o.total?.toFixed(2)}</div>
                  </div>
                ))}
            </>
          )}
          {tab === "downloads" && (
            <>
              <h2 className="font-display text-2xl mb-6">Your Downloads</h2>
              {orders.filter((o) => o.payment_status === "paid").length === 0 ? <p className="text-gray-500">Purchases will appear here as downloadable items.</p> :
                orders.filter((o) => o.payment_status === "paid").flatMap((o) => o.items?.map((it) => (
                  <div key={`${o.id}-${it.product_id}`} className="flex justify-between items-center py-4 border-b border-white/5">
                    <span>{it.name}</span>
                    <button data-testid={`dl-${it.product_id}`} onClick={() => download(o.id, it.product_id)} className="text-[#D4AF37] text-sm flex items-center gap-2"><Download size={14} /> Download</button>
                  </div>
                )))}
            </>
          )}
          {tab === "wishlist" && (
            <>
              <h2 className="font-display text-2xl mb-6">Your Wishlist</h2>
              {wishlist.length === 0 ? <p className="text-gray-500">Nothing saved yet.</p> :
                <div className="grid sm:grid-cols-2 gap-4">{wishlist.map((p) => <ProductCard key={p.id} product={p} />)}</div>
              }
            </>
          )}
          {tab === "profile" && (
            <>
              <h2 className="font-display text-2xl mb-6">Profile</h2>
              <div className="space-y-3 text-sm">
                <div><span className="text-gray-500">Name:</span> {user.name}</div>
                <div><span className="text-gray-500">Email:</span> {user.email}</div>
                <div><span className="text-gray-500">Role:</span> {user.role}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
